import { useCallback, useRef, useState } from 'react';

/**
 * Union type for all fetch lifecycle states.
 * Using a discriminated union instead of separate boolean flags (isLoading, isError)
 * eliminates impossible states like { isLoading: true, isError: true }.
 */
export type FetchState = 'idle' | 'loading' | 'retrying' | 'error' | 'success';

interface UseFetchWithRetryOptions {
  /** Maximum number of attempts before giving up. Default: 3 */
  maxRetries?: number;
  /** Milliseconds to wait between retries. Default: 3000 */
  retryDelayMs?: number;
  /** Return false to stop retrying immediately (e.g. session already completed). */
  shouldRetry?: (error: unknown) => boolean;
}

interface UseFetchWithRetryResult<T> {
  data: T | null;
  fetchState: FetchState;
  /** 1-based current attempt number, useful for "Próba 2/3..." UI */
  attempt: number;
  /** Trigger (or re-trigger) the fetch. Idempotent – cancels any in-flight retry. */
  execute: () => void;
}

/**
 * Generic hook that wraps an async fetcher function with automatic retry logic.
 *
 * Design decisions:
 * - Generic <T> so the same hook can be reused for fetching questions AND
 *   submitting answers (both need retry on weak Wi-Fi).
 * - `useRef` for the timeout ID ensures cleanup across renders without
 *   requiring the timeout to be a dependency of any effect.
 * - `execute` is memoised with `useCallback` – safe to use in useEffect
 *   dependency arrays without triggering infinite loops.
 *
 * @example
 * const { data, fetchState, attempt, execute } = useFetchWithRetry(
 *   () => fetch('/api/v1/questions').then(r => r.json()),
 *   { maxRetries: 3, retryDelayMs: 3000 }
 * );
 */
export function useFetchWithRetry<T>(
  fetcher: () => Promise<T>,
  { maxRetries = 3, retryDelayMs = 3000, shouldRetry }: UseFetchWithRetryOptions = {},
): UseFetchWithRetryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [attempt, setAttempt] = useState(1);

  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(1);

  const cancel = useCallback(() => {
    if (retryTimeoutRef.current !== null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (currentAttempt: number) => {
      setFetchState(currentAttempt === 1 ? 'loading' : 'retrying');
      setAttempt(currentAttempt);

      try {
        const result = await fetcher();
        setData(result);
        setFetchState('success');
      } catch (err) {
        // TODO: In production, differentiate network errors from server errors
        // (4xx should NOT retry; 5xx / network errors should).
        console.warn(
          `[useFetchWithRetry] Attempt ${currentAttempt}/${maxRetries} failed:`,
          err,
        );

        const canRetry = shouldRetry?.(err) ?? true;
        if (canRetry && currentAttempt < maxRetries) {
          retryTimeoutRef.current = setTimeout(() => {
            attemptRef.current = currentAttempt + 1;
            void run(currentAttempt + 1);
          }, retryDelayMs);
        } else {
          setFetchState('error');
        }
      }
    },
    // fetcher is expected to be stable (defined outside component or memoised).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxRetries, retryDelayMs],
  );

  const execute = useCallback(() => {
    cancel();
    attemptRef.current = 1;
    void run(1);
  }, [cancel, run]);

  return { data, fetchState, attempt, execute };
}
